import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Container,
  Grid,
  Modal,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { formatInTimeZone } from "date-fns-tz";
import { useFormik } from "formik";
import { DatePicker } from "@mui/x-date-pickers";

import { FooterActionsButtons, Page, ScreenHeader, Table } from "@APP/components";
import { SCREEN_PATHS } from "@APP/navigation";
import { BankObject, GracePeriod, OrganisationData, User } from "@APP/types";
import {
  getGracePeriod,
  getOrganizationById,
  getOrgBills,
  setGracePeriod,
} from "@APP/services/api";
import { formatErrorMessage, getSubscriptionStatus } from "@APP/utils";
import { useAlert, useHandleErrorCodes } from "@APP/hooks";
import { hideLoader, showLoader } from "@APP/redux";
import { tableStyles as useStyles } from "@APP/styles";
import { commonFormatDate } from "@APP/constants";
import CONFIG from "@APP/config";
import { API } from "@APP/services";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "30%",
  bgcolor: "background.paper",
  borderRadius: "8px",
  p: 4,
};

interface InvoiceCreatedAction {
  erpId: string;
  createdAt: string;
}

interface RtpCreatedAction {
  createdAt: string;
}

interface BillingActions {
  invoiceCreated: InvoiceCreatedAction;
  rtpCreated: RtpCreatedAction;
}

interface AuditEntry {
  timestamp: string;
  status: string;
  message?: string;
}
interface BillingRecord {
  issueDate: string;
  nextBillingDate: string;
  status: string;
  actions: BillingActions;
  audit: AuditEntry[];
}
interface BillingPaginationMeta {
  totalPages?: number | null;
  totalItems: number;
}
interface NotificationAuditEntry {
  timestamp: string;
  status: string;
  message?: string;
}
interface NotificationRecord {
  notificationDate: string;
  type: string;
  status: string;
  audit: NotificationAuditEntry[];
}

interface NotificationPaginationMeta {
  totalPages?: number | null;
  totalItems: number;
}

const OrgDetailsView = () => {
  const history = useHistory();
  const alert = useAlert();
  const dispatch = useDispatch();
  const { orgId }: { orgId: string } = useParams();
  const classes = useStyles();
  const handleErrorCodes = useHandleErrorCodes();
  const [orgDetails, setOrgDetails] = useState<OrganisationData | null>(null);
  const [bankDetails, setBankDetails] = useState<BankObject | null>(null);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [showGracePeriod, setShowGracePeriod] = useState(false);
  const [gracePeriodId, setGracePeriodId] = useState("");
  const [bills, setBills] = useState<BillingRecord[]>([]);
  const [billsMeta, setBillsMeta] = useState<BillingPaginationMeta>();
  const [orgReminders, setOrgReminders] = useState<NotificationRecord[]>([]);
  const [notificationsMeta, setNotificationsMeta] = useState<NotificationPaginationMeta>();
  const [orgReminderPage, setOrgReminderPage] = useState<number>(0);
  const [orgReminderEntries, setOrgReminderEntries] = useState<number>(25);
  const [orgReminderLastPage, setOrgReminderLastPage] = useState<number>(0);
  const [billsPage, setBillsPage] = useState<number>(0);
  const [billsEntries, setBillsEntries] = useState<number>(25);
  const [billsLastPage, setBillsLastPage] = useState<number>(0);
  const [gracePeriodInfo, setGracePeriodInfo] = useState<GracePeriod>();
  const { subscriptionStatus } = orgDetails || {};

  const subscriptionLabel = ((status) => {
    switch (status) {
      case "Trial":
      case "Active":
        return "Deactivate";
      case "PendingCancellation":
      case "Cancelled":
        return "Activate";
      case "Suspended":
        return "Active";
      default:
        return "Change";
    }
  })(subscriptionStatus);

  const getOrgDetailsById = async () => {
    dispatch(showLoader());
    try {
      const details = await getOrganizationById(orgId);
      setOrgDetails(details);
    } catch (error) {
      alert.open("Error", formatErrorMessage(error), [
        { text: "Okay", onClick: () => history.push(SCREEN_PATHS.USER_DASHBOARD) },
      ]);
    } finally {
      dispatch(hideLoader());
    }
  };

  const changeSubscription = async () => {
    try {
      dispatch(showLoader());

      let subsId = "";
      const isActivate = subscriptionLabel === "Activate";
      const isActive = subscriptionLabel === "Active";

      if (isActivate) {
        const billingPlans = await API.getSubscriptionsBillingPlans(orgId);

        if (!billingPlans?.length) {
          throw new Error("No billing plans available");
        }
        const availablePlan = billingPlans.find((plan) => plan.available);
        subsId = availablePlan?.id ?? "";
      } else {
        const subscription = await API.getSubscriptionsByOrgId(orgId);
        subsId = subscription.id;
      }
      if (isActive) await API.activateSubscription(orgId);
      else await API.changeOrganisationSubscriptionStatus(isActivate, orgId, subsId);
      getOrgDetailsById();
    } catch (error: any) {
      const errorData = error?.response?.data;
      const isHandled = handleErrorCodes(errorData?.errorCode);
      if (!isHandled) {
        alert.open("Error", formatErrorMessage(error));
      }
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleSubscription = () => {
    alert.open(
      "Confirm",
      `Are you sure you want to ${subscriptionLabel.toLocaleLowerCase()} this subscription?`,
      [{ text: "No" }, { text: "Yes", onClick: changeSubscription }],
    );
  };

  const fetchGracePeriod = async () => {
    let subsId;
    try {
      dispatch(showLoader());
      const subscription = await API.getSubscriptionsByOrgId(orgId);
      subsId = subscription.id;
      const response = await getGracePeriod(orgId, subsId);
      setGracePeriodId(response?.data[0]?.id);
      setGracePeriodInfo(response?.data[0]);
      if (response?.data?.length > 0) setShowGracePeriod(false);
      else setShowGracePeriod(true);
    } catch (error) {
      setShowGracePeriod(true);
      // alert.open("Error", formatErrorMessage(error));
    } finally {
      dispatch(hideLoader());
    }
  };

  const giveGracePeriod = async () => {
    let subsId;
    try {
      dispatch(showLoader());
      const subscription = await API.getSubscriptionsByOrgId(orgId);
      subsId = subscription.id;
    } catch (error) {
      alert.open("Error", formatErrorMessage(error));
    } finally {
      dispatch(hideLoader());
    }

    const graceData = {
      orgId: orgId,
      planId: subsId,
      from: values.from,
      until: values.to,
    };

    try {
      dispatch(showLoader());
      await setGracePeriod(graceData);
      handleClose();
      window.location.reload();
    } catch (error) {
      alert.open("Error", formatErrorMessage(error));
    } finally {
      dispatch(hideLoader());
    }
  };

  const { errors, handleSubmit, touched, values, setFieldValue } = useFormik({
    initialValues: {
      from: new Date().toISOString(),
      to: new Date().toISOString(),
    },
    onSubmit: giveGracePeriod,
  });

  const handleGracePeriod = () => {
    handleOpen();
  };

  const deleteGracePeriod = async () => {
    dispatch(showLoader());
    try {
      await API.deleteGracePeriod(gracePeriodId, orgId);
      window.location.reload();
    } catch (error) {
      alert.open("Error", formatErrorMessage(error));
    } finally {
      dispatch(hideLoader());
    }
  };

  const getUserBankDetails = async () => {
    dispatch(showLoader());
    try {
      const response = await API.getBankDetails(orgId);
      setBankDetails(response);
    } catch (error) {
      alert.open("Error", formatErrorMessage(error), [
        { text: "Okay", onClick: () => history.push(SCREEN_PATHS.USER_DASHBOARD) },
      ]);
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleOnOrgReminderEntriesChange = (entries: number) => {
    setOrgReminderPage(0);
    setOrgReminderEntries(entries);
    notificationsMeta && setOrgReminderLastPage(Math.ceil(notificationsMeta?.totalItems / entries));
  };

  const handleBillsEntrieschange = (entries: number) => {
    setBillsPage(0);
    setBillsEntries(entries);
    billsMeta && setBillsLastPage(Math.ceil(billsMeta?.totalItems / entries));
  };

  const fetchReminders = async () => {
    const pagination = { page: orgReminderPage, entries: orgReminderEntries };

    dispatch(showLoader());
    try {
      const { data, meta } = await API.getReminders(orgId, pagination);
      setOrgReminders(data);
      setNotificationsMeta(meta);
      if (meta?.totalItems) {
        if (meta?.totalItems <= orgReminderEntries) {
          setOrgReminderLastPage(1);
        } else {
          const lastPageIndex = Math.ceil(meta?.totalItems / orgReminderEntries);
          setOrgReminderLastPage(lastPageIndex);
        }
      }
    } catch (error) {
      // do nothing for now
    } finally {
      dispatch(hideLoader());
    }
  };

  const fetchOrgBills = async () => {
    const pagination = { page: billsPage, entries: billsEntries };
    dispatch(showLoader());
    try {
      const { data, meta } = await getOrgBills(orgId, pagination);
      setBills(data);
      setBillsMeta(meta);
      if (meta?.totalItems) {
        if (meta?.totalItems <= billsEntries) {
          setBillsLastPage(1);
        } else {
          const lastPageIndex = Math.ceil(meta?.totalItems / billsEntries);
          setBillsLastPage(lastPageIndex);
        }
      }
    } catch (error) {
      // do nothing for now
    } finally {
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    getOrgDetailsById();
    getUserBankDetails();
    fetchGracePeriod();
    fetchOrgBills();
    fetchReminders();
  }, []);

  const renderHeader = () => (
    <TableRow className={classes.tableHead}>
      <TableCell>Username</TableCell>
      <TableCell>Status</TableCell>
      <TableCell>Created Date</TableCell>
      <TableCell>Created Time</TableCell>
    </TableRow>
  );

  const renderRows = ({ id, username, status, meta: { createdAt } }: User) => (
    <TableRow key={id}>
      <TableCell>{username}</TableCell>
      <TableCell>{status}</TableCell>
      <TableCell>
        {createdAt ? formatInTimeZone(createdAt ?? "", "UTC", commonFormatDate) : "-"}
      </TableCell>
      <TableCell>{createdAt ? formatInTimeZone(createdAt ?? "", "UTC", "HH:mm") : "-"}</TableCell>
    </TableRow>
  );

  const renderEmptyDataRow = () => (
    <TableRow>
      <TableCell colSpan={7} align="center">
        User Details not found.
      </TableCell>
    </TableRow>
  );

  const renderBillsHeader = () => (
    <TableRow className={classes.tableHead}>
      <TableCell>RTPs sent</TableCell>
      <TableCell>RTP issued datetime</TableCell>
      <TableCell>RTPs status audit</TableCell>
      <TableCell>Next billing date</TableCell>
    </TableRow>
  );

  const renderBillsRows = ({ actions, issueDate, nextBillingDate, audit }: BillingRecord) => (
    <TableRow key={issueDate}>
      <TableCell>
        {actions?.rtpCreated?.createdAt
          ? `${formatInTimeZone(actions?.rtpCreated?.createdAt ?? "", "UTC", commonFormatDate)}`
          : "-"}{" "}
      </TableCell>
      <TableCell>
        {issueDate
          ? `${formatInTimeZone(issueDate ?? "", "UTC", commonFormatDate)} - ${formatInTimeZone(
              issueDate ?? "",
              "UTC",
              "HH:mm",
            )}`
          : "-"}
      </TableCell>
      <TableCell>
        {audit.map((aud: AuditEntry) => (
          <Typography>{aud.status}</Typography>
        ))}
      </TableCell>
      <TableCell>
        {nextBillingDate
          ? `${formatInTimeZone(nextBillingDate ?? "", "UTC", commonFormatDate)}`
          : "-"}
      </TableCell>
    </TableRow>
  );

  const renderEmptySubsDataRow = () => (
    <TableRow>
      <TableCell colSpan={7} align="center">
        User Subscription not found.
      </TableCell>
    </TableRow>
  );

  const renderOrgHeader = () => (
    <TableRow className={classes.tableHead}>
      <TableCell>Notification sent</TableCell>
      <TableCell>Notification date-time</TableCell>
      <TableCell>Notification Type</TableCell>
    </TableRow>
  );

  const renderOrgRows = ({ status, notificationDate, type }: NotificationRecord) => (
    <TableRow>
      <TableCell>
        <Typography>{status === "Sent" ? "Yes" : "No"}</Typography>
      </TableCell>
      <TableCell>
        {notificationDate
          ? `${formatInTimeZone(
              notificationDate ?? "",
              "UTC",
              commonFormatDate,
            )} - ${formatInTimeZone(notificationDate ?? "", "UTC", "HH:mm")}`
          : "-"}
      </TableCell>
      <TableCell>{type}</TableCell>
    </TableRow>
  );

  const renderEmptyOrgRemindersDataRow = () => (
    <TableRow>
      <TableCell colSpan={7} align="center">
        User Notifications not found.
      </TableCell>
    </TableRow>
  );

  return (
    <Container maxWidth={false}>
      <Page title="User Details">
        <Grid container>
          <Grid item xs={12} md={6}>
            <ScreenHeader title="View Organisation" />
          </Grid>
          {CONFIG.FEATURES?.SUBSCRIPTION && orgDetails?.subscriptionStatus !== null && (
            <Grid item xs={12} md={6}>
              <Box display="flex" textAlign="center" justifyContent="flex-end" gap={2}>
                {showGracePeriod ? (
                  <Button variant="contained" color="primary" onClick={handleGracePeriod}>
                    Give grace period
                  </Button>
                ) : (
                  <Button variant="contained" color="error" onClick={deleteGracePeriod}>
                    Remove grace period
                  </Button>
                )}
                <Button variant="contained" color="primary" onClick={handleSubscription}>
                  {`${subscriptionLabel} Subscription`}
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
        <form>
          <Box my={3}>
            <Card elevation={12}>
              <Grid container spacing={3}>
                <Grid item xs={12} m="16px 16px 0px">
                  <Card elevation={2} sx={{ padding: "12px" }}>
                    <Grid container display="flex" spacing={1} mb={1}>
                      <Grid item display="flex" xs={12}>
                        <Grid item xs={12} md={2}>
                          <Typography variant="h6">Organisation Name:</Typography>
                        </Grid>
                        <Grid item xs={12} md={10}>
                          <Typography variant="body2" color="textSecondary">
                            {orgDetails?.orgName}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid container display="flex" spacing={1} mb={1}>
                      <Grid item display="flex" xs={12}>
                        <Grid item xs={12} md={2}>
                          <Typography variant="h6">Status:</Typography>
                        </Grid>
                        <Grid item xs={12} md={10}>
                          <Typography variant="body2" color="textSecondary">
                            {orgDetails?.status}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid container display="flex" spacing={1} mb={1}>
                      <Grid item display="flex" xs={12}>
                        <Grid item xs={12} md={2}>
                          <Typography variant="h6">Subscription Status:</Typography>
                        </Grid>
                        <Grid item xs={12} md={10}>
                          <Typography variant="body2" color="textSecondary">
                            {getSubscriptionStatus(orgDetails?.subscriptionStatus ?? "-")}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid container display="flex" spacing={1} mb={1}>
                      <Grid item display="flex" xs={12}>
                        <Grid item xs={12} md={2}>
                          <Typography variant="h6">Created Date:</Typography>
                        </Grid>
                        <Grid item xs={12} md={10}>
                          <Typography variant="body2" color="textSecondary">
                            {orgDetails?.createdAt
                              ? formatInTimeZone(
                                  orgDetails?.createdAt ?? "",
                                  "UTC",
                                  commonFormatDate,
                                )
                              : "-"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid container display="flex" spacing={1} mb={1}>
                      <Grid item display="flex" xs={12}>
                        <Grid item xs={12} md={2}>
                          <Typography variant="h6">Created Time:</Typography>
                        </Grid>
                        <Grid item xs={12} md={10}>
                          <Typography variant="body2" color="textSecondary">
                            {orgDetails?.createdAt
                              ? formatInTimeZone(orgDetails?.createdAt ?? "", "UTC", "HH:mm")
                              : "-"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                <Grid item xs={12} m="16px 16px 0px">
                  <Card elevation={2} sx={{ padding: "12px" }}>
                    {/* MH User Id */}
                    <Grid container display="flex" spacing={1} mb={1}>
                      <Grid item display="flex" xs={12}>
                        <Grid item xs={12} md={2}>
                          <Typography variant="h6">MH User Id:</Typography>
                        </Grid>
                        <Grid item xs={12} md={10}>
                          <Typography variant="body2" color="textSecondary">
                            {bankDetails?.externalUserId ?? "-"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Org Id */}
                    <Grid container display="flex" spacing={1} mb={1}>
                      <Grid item display="flex" xs={12}>
                        <Grid item xs={12} md={2}>
                          <Typography variant="h6">Org Id:</Typography>
                        </Grid>
                        <Grid item xs={12} md={10}>
                          <Typography variant="body2" color="textSecondary">
                            {orgDetails?.orgId ?? "-"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Linked Bank Accounts */}
                    <Grid container display="flex" spacing={1} mb={1}>
                      <Grid item display="flex" xs={12}>
                        <Grid item xs={12} md={2}>
                          <Typography variant="h6">Linked Bank Accounts:</Typography>
                        </Grid>
                        <Grid item xs={12} md={10}>
                          <Typography variant="body2" color="textSecondary">
                            {bankDetails?.linkedBanks
                              ?.flatMap((bank) =>
                                bank.bankDetails.map(
                                  (detail) =>
                                    `${detail.account.identification} (${detail.accountType})`,
                                ),
                              )
                              .join(", ") ?? "-"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Organization Type */}
                    <Grid container display="flex" spacing={1} mb={1}>
                      <Grid item display="flex" xs={12}>
                        <Grid item xs={12} md={2}>
                          <Typography variant="h6">Organization Type:</Typography>
                        </Grid>
                        <Grid item xs={12} md={10}>
                          <Typography variant="body2" color="textSecondary">
                            {orgDetails?.orgType ?? "-"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* ERP Linked */}
                    <Grid container display="flex" spacing={1} mb={1}>
                      <Grid item display="flex" xs={12}>
                        <Grid item xs={12} md={2}>
                          <Typography variant="h6">ERP Linked:</Typography>
                        </Grid>
                        <Grid item xs={12} md={10}>
                          <Typography variant="body2" color="textSecondary">
                            {orgDetails?.erpId ?? "-"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid container display="flex" spacing={1} mb={1}>
                      <Grid item display="flex" xs={12}>
                        <Grid item xs={12} md={2}>
                          <Typography variant="h6">Grace period start date:</Typography>
                        </Grid>
                        <Grid item xs={12} md={10}>
                          <Typography variant="body2" color="textSecondary">
                            {gracePeriodInfo?.from
                              ? formatInTimeZone(
                                  gracePeriodInfo?.from ?? "-",
                                  "UTC",
                                  commonFormatDate,
                                )
                              : "-"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid container display="flex" spacing={1} mb={1}>
                      <Grid item display="flex" xs={12}>
                        <Grid item xs={12} md={2}>
                          <Typography variant="h6">Grace period end date:</Typography>
                        </Grid>
                        <Grid item xs={12} md={10}>
                          <Typography variant="body2" color="textSecondary">
                            {gracePeriodInfo?.until
                              ? formatInTimeZone(
                                  gracePeriodInfo?.until ?? "-",
                                  "UTC",
                                  commonFormatDate,
                                )
                              : "-"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Typography p={2} variant="h4">
                    Subscription Billing History
                  </Typography>
                  <Table
                    data={bills || []}
                    renderHeader={renderBillsHeader}
                    renderRows={renderBillsRows}
                    renderEmptyDataRow={renderEmptySubsDataRow}
                    page={billsPage}
                    entries={billsEntries}
                    onPageChange={setBillsPage}
                    onEntriesChange={handleBillsEntrieschange}
                    lastPage={billsLastPage}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography p={2} variant="h4">
                    Subscription Notification History
                  </Typography>

                  <Table
                    data={orgReminders || []}
                    renderHeader={renderOrgHeader}
                    renderRows={renderOrgRows}
                    renderEmptyDataRow={renderEmptyOrgRemindersDataRow}
                    page={orgReminderPage}
                    entries={orgReminderEntries}
                    onPageChange={setOrgReminderPage}
                    onEntriesChange={handleOnOrgReminderEntriesChange}
                    lastPage={orgReminderLastPage}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Table
                    data={orgDetails?.users || []}
                    renderHeader={renderHeader}
                    renderRows={renderRows}
                    showPagination={false}
                    renderEmptyDataRow={renderEmptyDataRow}
                  />
                </Grid>
              </Grid>
              <Box p={2}>
                <Grid container item>
                  <Grid item lg={4} xs={false} />
                </Grid>
              </Box>
            </Card>
            <FooterActionsButtons backButtonText="Back" handleBackButton={() => history.goBack()} />
          </Box>
        </form>
      </Page>
      <Modal
        open={open}
        aria-labelledby="grace-modal-title"
        aria-describedby="grace-modal-description">
        <form onSubmit={handleSubmit}>
          <Box sx={style}>
            <Typography id="grace-modal-title" variant="h3" component="h2">
              Grace Period
            </Typography>
            <Typography variant="body2" id="grace-modal-description" sx={{ mt: 2 }}>
              Please select the start and end dates for the grace period.
            </Typography>
            <Grid
              container
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              gap={2}
              mt={2}>
              <Grid item display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1">From:</Typography>
                <DatePicker
                  renderInput={(props) => (
                    <TextField
                      error={Boolean(touched.from && errors.from)}
                      helperText={touched.from && errors.from}
                      // fullWidth
                      sx={{ width: "80%" }}
                      size="small"
                      onClick={() => setStartDatePickerOpen(true)}
                      {...props}
                    />
                  )}
                  onChange={(value) => {
                    setFieldValue("from", value);
                  }}
                  value={values.from}
                  label="Start Date"
                  disablePast
                  inputFormat={commonFormatDate}
                  open={startDatePickerOpen}
                  onOpen={() => setStartDatePickerOpen(true)}
                  onClose={() => setStartDatePickerOpen(false)}
                />
              </Grid>
              <Grid item display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1">To:</Typography>
                <DatePicker
                  renderInput={(props) => (
                    <TextField
                      error={Boolean(touched.to && errors.to)}
                      helperText={touched.to && errors.to}
                      // fullWidth
                      sx={{ width: "80%" }}
                      size="small"
                      onClick={() => setEndDatePickerOpen(true)}
                      label="End Date"
                      {...props}
                    />
                  )}
                  onChange={(value) => {
                    setFieldValue("to", value);
                  }}
                  value={values.to}
                  disablePast
                  label="End Date"
                  inputFormat={commonFormatDate}
                  open={endDatePickerOpen}
                  onOpen={() => setEndDatePickerOpen(true)}
                  onClose={() => setEndDatePickerOpen(false)}
                />
              </Grid>
            </Grid>
            <Stack direction="row" spacing={2} mt={2}>
              <Button onClick={handleClose} fullWidth variant="outlined">
                Cancel
              </Button>
              <Button fullWidth type="submit" variant="contained">
                Save
              </Button>
            </Stack>
          </Box>
        </form>
      </Modal>
    </Container>
  );
};

export default OrgDetailsView;
